import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Button, Card, Grid, IconButton, Chip, TextField,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Alert as MuiAlert, Snackbar, Menu, MenuItem,
  FormControl, InputLabel, Select,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useThemeMode, MONO } from '../../context/ThemeContext';
import { useQueryClient } from '@tanstack/react-query';
import { useUsers } from '../../services/queries';
import { inviteUser, resendInvite, updateUser, deleteUser } from '../../services/admin';
import { PageHeader, LoadingSkeleton, StatCard, FilterSelect, ConfirmDialog, EmptyState } from '../../components/common';
import { useRecipes } from '../../theme/recipes';
import { fmtDate } from '../../utils/format';

// Mirrors VALID_PLANS in auth-svc, which rejects anything else.
const PLANS = ['Free', 'Standard', 'Premium'];

// 'premium' is the stored role for every account that is not an admin. It
// dates from when there was one paid tier, and it does NOT mean the Premium
// plan: a Free customer and a Premium customer both carry it. Showing the raw
// value made the Role column contradict the Plan column beside it, with a
// Standard subscriber labelled PREMIUM. The stored value stays as it is,
// because it appears in tokens and in the API, but nobody has to read it.
const roleLabel = (role) => (role === 'admin' ? 'Administrator' : 'Customer');


function UserManagement() {
  const { palette } = useThemeMode();
  const recipes = useRecipes();
  const queryClient = useQueryClient();
  const { data: users = [], isLoading } = useUsers();
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [menuFor, setMenuFor] = useState(null); // { anchor, user }
  const [planFor, setPlanFor] = useState(null);   // user whose plan is being changed
  const [planChoice, setPlanChoice] = useState('Free');
  const [savingPlan, setSavingPlan] = useState(false);
  const [confirmUser, setConfirmUser] = useState(null);
  const [toast, setToast] = useState(null);
  const [invite, setInvite] = useState({ email: '', name: '', role: 'premium', plan: 'Standard' });

  const ctaSx = { ...recipes.primaryBtnSx, px: 2.5, py: 0.9, fontSize: '0.875rem', textTransform: 'none' };

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['users'] });

  const filtered = useMemo(() => users.filter((u) => {
    if (filterRole !== 'all' && u.role !== filterRole) return false;
    if (search) {
      const s = search.toLowerCase();
      return u.name.toLowerCase().includes(s) || u.email.toLowerCase().includes(s);
    }
    return true;
  }), [users, search, filterRole]);

  const stats = useMemo(() => ({
    total: users.length,
    admins: users.filter((u) => u.role === 'admin').length,
    premium: users.filter((u) => u.role === 'premium').length,
    active: users.filter((u) => u.status === 'active').length,
  }), [users]);

  const sendInvite = async () => {
    if (!invite.email || !invite.name) { setToast({ severity: 'error', msg: 'Fill in name and email.' }); return; }
    try {
      const created = await inviteUser({ name: invite.name, email: invite.email, role: invite.role, plan: invite.plan });
      refresh();
      const to = invite.email;
      setInvite({ email: '', name: '', role: 'premium', plan: 'Standard' });
      setInviteOpen(false);
      // The endpoint reports whether the mail actually left. Saying "invited"
      // when nothing was sent leaves an admin waiting for a reply that cannot
      // come, so the two cases read differently.
      setToast(created?.inviteEmailSent === false
        ? { severity: 'warning', msg: `Account created for ${to}, but the invitation email did not send. Use Resend invitation once mail is working.` }
        : { severity: 'success', msg: `Invitation sent to ${to}. The link is good for 72 hours.` });
    } catch (e) {
      setToast({ severity: 'error', msg: e?.message || 'Invite failed.' });
    }
  };

  const resend = async (user) => {
    setMenuFor(null);
    try {
      const res = await resendInvite(user.id);
      setToast(res?.inviteEmailSent === false
        ? { severity: 'warning', msg: `A new link was created for ${user.email}, but the email did not send.` }
        : { severity: 'success', msg: `Invitation resent to ${user.email}. The new link is good for 72 hours, and the old one no longer works.` });
    } catch (e) {
      setToast({ severity: 'error', msg: e?.message || 'Could not resend that invitation.' });
    }
  };

  const toggleStatus = async (user) => {
    setMenuFor(null);
    try {
      await updateUser(user.id, { status: user.status === 'active' ? 'inactive' : 'active' });
      refresh();
      setToast({ severity: 'info', msg: `${user.name} ${user.status === 'active' ? 'deactivated' : 'reactivated'}.` });
    } catch (e) {
      setToast({ severity: 'error', msg: e?.message || 'Update failed.' });
    }
  };

  const switchRole = async (user) => {
    setMenuFor(null);
    const newRole = user.role === 'admin' ? 'premium' : 'admin';
    try {
      await updateUser(user.id, { role: newRole });
      refresh();
      setToast({ severity: 'info', msg: `${user.name} is now a ${newRole}.` });
    } catch (e) {
      setToast({ severity: 'error', msg: e?.message || 'Update failed.' });
    }
  };

  const openPlan = (user) => {
    setMenuFor(null);
    setPlanChoice(PLANS.includes(user.plan) ? user.plan : 'Free');
    setPlanFor(user);
  };

  const savePlan = async () => {
    if (!planFor || planChoice === planFor.plan) { setPlanFor(null); return; }
    setSavingPlan(true);
    try {
      await updateUser(planFor.id, { plan: planChoice });
      refresh();
      setToast({
        severity: 'success',
        // Plan travels in the access token, so a signed-in user keeps their
        // old entitlements until it refreshes. Saying so stops an admin
        // thinking the change failed when the customer reports no difference.
        msg: `${planFor.name || planFor.email} moved to ${planChoice}. It applies to them within 15 minutes, or immediately if they sign in again.`,
      });
      setPlanFor(null);
    } catch (e) {
      setToast({ severity: 'error', msg: e?.message || 'Could not change the plan.' });
    } finally {
      setSavingPlan(false);
    }
  };

  const remove = async (user) => {
    try {
      await deleteUser(user.id);
      refresh();
      setToast({ severity: 'info', msg: `${user.name} removed.` });
    } catch (e) {
      setToast({ severity: 'error', msg: e?.message || 'Delete failed.' });
    }
  };

  if (isLoading) {
    return <Box sx={{ p: 1 }}><LoadingSkeleton variant="page" /></Box>;
  }

  return (
    <Box>
      <PageHeader
        eyebrow="Admin"
        title="User Management"
        subtitle="Invite users, manage roles, and control access to the platform"
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'Users' }
        ]}
        actions={
          <Button
            variant="contained"
            disableElevation
            startIcon={<Icon icon="ph:user-plus" width={16} height={16} />}
            onClick={() => setInviteOpen(true)}
            sx={ctaSx}
          >
            Invite user
          </Button>
        }
      />

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Users', value: stats.total, tone: 'info', icon: 'ph:users' },
          { label: 'Admins', value: stats.admins, tone: 'warning', icon: 'ph:shield-star' },
          { label: 'Customers', value: stats.premium, tone: 'accent', icon: 'ph:user' },
          { label: 'Active', value: stats.active, tone: 'teal', icon: 'ph:check-circle' },
        ].map((s) => (
          <Grid item xs={6} md={3} key={s.label}>
            <StatCard label={s.label} value={s.value} tone={s.tone} icon={<Icon icon={s.icon} width={20} height={20} />} />
          </Grid>
        ))}
      </Grid>

      {/* Filter bar */}
      <Card sx={{ p: 1.5, mb: 2 }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name or email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={recipes.searchSx}
              inputProps={{ 'aria-label': 'Search users by name or email' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon icon="ph:magnifying-glass" width={17} height={17} color={palette.textMuted} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FilterSelect
              label="Role"
              value={filterRole}
              onChange={setFilterRole}
              options={[
                { value: 'all', label: 'All roles' },
                { value: 'admin', label: 'Administrator' },
                { value: 'premium', label: 'Customer' },
              ]}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography sx={{ fontSize: '0.8rem', color: palette.textMuted, textAlign: { md: 'right' }, fontFamily: MONO }}>
              Showing {filtered.length} of {users.length} users
            </Typography>
          </Grid>
        </Grid>
      </Card>

      {/* Table */}
      <TableContainer component={Card}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {[
                { label: 'User', align: 'left' },
                { label: 'Role', align: 'left' },
                { label: 'Plan', align: 'left' },
                { label: 'Status', align: 'left' },
                { label: 'Last Login', align: 'left' },
                { label: 'Actions', align: 'right' },
              ].map((col) => (
                <TableCell key={col.label} align={col.align} sx={recipes.headCellSx}>
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((u) => (
              <TableRow key={u.id} hover sx={recipes.bodyRowSx}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                    <Box sx={{ width: 34, height: 34, borderRadius: '50%', bgcolor: u.role === 'admin' ? palette.warningBg : palette.accentBg, color: u.role === 'admin' ? palette.warning : palette.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.82rem', flexShrink: 0 }}>
                      {u.name.split(' ').map((p) => p[0]).slice(0, 2).join('')}
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.88rem', color: palette.text }}>{u.name}</Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: palette.textMuted }}>{u.email}</Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    icon={<Icon icon={u.role === 'admin' ? 'ph:shield-star' : 'ph:user'} width={12} height={12} />}
                    label={roleLabel(u.role).toUpperCase()}
                    sx={{ height: 22, fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.05em', bgcolor: u.role === 'admin' ? palette.warningBg : palette.accentBg, color: u.role === 'admin' ? palette.warning : palette.accent, '& .MuiChip-icon': { color: 'inherit' } }}
                  />
                </TableCell>
                <TableCell sx={{ fontSize: '0.82rem' }}>{u.plan}</TableCell>
                <TableCell>
                  <Chip label={u.status.toUpperCase()} size="small"
                    sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.05em',
                      bgcolor: u.status === 'active' ? palette.accentBg : (u.status === 'pending' ? palette.infoBg : palette.surfaceHover),
                      color: u.status === 'active' ? palette.accent : (u.status === 'pending' ? palette.info : palette.textMuted) }} />
                </TableCell>
                <TableCell sx={{ fontFamily: MONO, fontSize: '0.78rem', color: palette.textSecondary }}>{fmtDate(u.lastLogin)}</TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    aria-label={`Actions for ${u.name}`}
                    onClick={(e) => setMenuFor({ anchor: e.currentTarget, user: u })}
                    sx={{ color: palette.textMuted }}
                  >
                    <Icon icon="ph:dots-three-vertical" width={16} height={16} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} sx={{ border: 0, p: 2.5 }}>
                  <EmptyState
                    compact
                    variant="dashed"
                    icon={<Icon icon="ph:users" width={24} height={24} />}
                    title="No matching users"
                    description="No users match the current search or role filter."
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Row actions menu */}
      <Menu
        anchorEl={menuFor?.anchor}
        open={!!menuFor}
        onClose={() => setMenuFor(null)}
        PaperProps={recipes.menuProps.PaperProps}
      >
        {menuFor?.user?.status === 'pending' && (
          <MenuItem onClick={() => resend(menuFor.user)} sx={{ fontSize: '0.85rem', color: palette.text, gap: 1 }}>
            <Icon icon="ph:envelope-simple-open" width={16} height={16} />
            Resend invitation
          </MenuItem>
        )}
        <MenuItem onClick={() => toggleStatus(menuFor.user)} sx={{ fontSize: '0.85rem', color: palette.text, gap: 1 }}>
          <Icon icon={menuFor?.user?.status === 'active' ? 'ph:prohibit' : 'ph:check-circle'} width={16} height={16} />
          {menuFor?.user?.status === 'active' ? 'Deactivate' : 'Reactivate'}
        </MenuItem>
        <MenuItem
          onClick={() => openPlan(menuFor.user)}
          disabled={menuFor?.user?.role === 'admin'}
          sx={{ fontSize: '0.85rem', color: palette.text, gap: 1 }}
        >
          <Icon icon="ph:credit-card" width={16} height={16} />
          Change plan
        </MenuItem>
        <MenuItem onClick={() => switchRole(menuFor.user)} sx={{ fontSize: '0.85rem', color: palette.text, gap: 1 }}>
          <Icon icon="ph:swap" width={16} height={16} />
          Switch to {menuFor?.user?.role === 'admin' ? 'Customer' : 'Administrator'}
        </MenuItem>
        <MenuItem
          onClick={() => { setConfirmUser(menuFor.user); setMenuFor(null); }}
          sx={{ fontSize: '0.85rem', color: palette.danger, gap: 1 }}
        >
          <Icon icon="ph:trash" width={16} height={16} />
          Remove user
        </MenuItem>
      </Menu>

      {/* Change plan */}
      <Dialog
        open={!!planFor}
        onClose={() => setPlanFor(null)}
        fullWidth
        PaperProps={{ sx: { ...recipes.dialogPaperProps.sx, maxWidth: 460, width: '100%' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>Change plan</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: '0.88rem', color: palette.textSecondary, mb: 2 }}>
            {planFor?.name || planFor?.email} is on <strong>{planFor?.plan}</strong>. Limits are
            enforced by the server, so this takes effect for real.
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel sx={recipes.labelSx}>Plan</InputLabel>
            <Select
              value={planChoice}
              label="Plan"
              onChange={(e) => setPlanChoice(e.target.value)}
              sx={recipes.controlSx}
              MenuProps={recipes.menuProps}
            >
              {PLANS.map((p) => (
                <MenuItem key={p} value={p} sx={{ fontSize: '0.87rem' }}>{p}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography sx={{ fontSize: '0.78rem', color: palette.textMuted, mt: 1.5, lineHeight: 1.6 }}>
            Free: 2 alerts, 3 holdings, no export. Standard: 20 alerts, unlimited holdings,
            5,000 exported rows a month, and the weekly digest. Premium adds unlimited
            everything.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setPlanFor(null)} sx={{ color: palette.textMuted, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={savePlan}
            disabled={savingPlan || planChoice === planFor?.plan}
            sx={ctaSx}
          >
            {savingPlan ? 'Saving…' : 'Change plan'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite dialog */}
      <Dialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={recipes.dialogPaperProps}
      >
        <DialogTitle sx={{ color: palette.text, fontWeight: 700 }}>Invite new user</DialogTitle>
        <DialogContent dividers sx={{ borderColor: palette.divider }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Full name"
              size="small"
              fullWidth
              value={invite.name}
              onChange={(e) => setInvite({ ...invite, name: e.target.value })}
              sx={recipes.searchSx}
              InputLabelProps={{ sx: recipes.labelSx }}
            />
            <TextField
              label="Email"
              type="email"
              size="small"
              fullWidth
              value={invite.email}
              onChange={(e) => setInvite({ ...invite, email: e.target.value })}
              sx={recipes.searchSx}
              InputLabelProps={{ sx: recipes.labelSx }}
            />
            <FilterSelect
              label="Role"
              value={invite.role}
              onChange={(v) => setInvite({ ...invite, role: v })}
              options={[
                { value: 'premium', label: 'Customer' },
                { value: 'admin', label: 'Administrator' },
              ]}
            />
            {invite.role === 'premium' && (
              <FilterSelect
                label="Plan"
                value={invite.plan}
                onChange={(v) => setInvite({ ...invite, plan: v })}
                options={[
                  { value: 'Free', label: 'Free' },
                  { value: 'Standard', label: 'Standard' },
                  { value: 'Premium', label: 'Premium' },
                ]}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteOpen(false)} sx={{ color: palette.textMuted, textTransform: 'none' }}>Cancel</Button>
          <Button onClick={sendInvite} variant="contained" disableElevation sx={ctaSx}>
            Send invitation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove confirmation */}
      <ConfirmDialog
        open={!!confirmUser}
        onClose={() => setConfirmUser(null)}
        onConfirm={() => { remove(confirmUser); setConfirmUser(null); }}
        title="Remove this user?"
        description={
          confirmUser
            ? `${confirmUser.name} (${confirmUser.email}) will lose access to the platform immediately. This cannot be undone.`
            : 'This user will lose access to the platform immediately.'
        }
        confirmLabel="Remove user"
        tone="danger"
        icon="ph:user-minus"
      />

      <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        {toast && <MuiAlert severity={toast.severity} onClose={() => setToast(null)}>{toast.msg}</MuiAlert>}
      </Snackbar>
    </Box>
  );
}

export default UserManagement;
